import { VendorTaxonomyKey } from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import getEnvironment, { EnvironmentTypes } from '@zola-helpers/client/dist/es/util/environment';

import capitalize from 'lodash/capitalize';

import { FacetChildKey, FacetParentKey } from '~/types/facets';
import {
  LocationOrAllMarketsType,
  SearchableOptionChildView,
  TaxonomyWithFacetsView,
} from '~/types/responseTypes';
import Logger from '~/util/logger';

import {
  CouplesSearchActions,
  RECEIVED_FACETS_V2,
} from '../../actions/types/couplesSearchActionTypes';

export interface FacetCategory {
  displayName: string;
  facets: SearchableFacet[];
  key: string;
  name: string;
  slug: string;
}

export interface SearchableFacet {
  displayName: string;
  key: string;
  name: string;
  parentKey: FacetParentKey;
  slug: string;
  description?: string | null;
}

export type FacetsByVendorType = {
  [key in VendorTaxonomyKey]?: Record<string, SearchableFacet>;
};

export type FacetsByTaxonomy = {
  [key in VendorTaxonomyKey]?: FacetCategory[];
};

export type VendorMarketKey = string;
export interface CouplesSearchState {
  facets: FacetsByTaxonomy;
  facetsByVendorType: FacetsByVendorType;
  recordedLocation: LocationOrAllMarketsType | Record<string, never>;
  locationKeyFromNav: string | null;
}

const initialState: CouplesSearchState = {
  // taxonomy-key => list of categories => list of facets
  facets: {},
  // taxonomy-key => facetSlug => facet
  facetsByVendorType: {},

  recordedLocation: {},
  locationKeyFromNav: null,
};

/**
  The keys are the names of the facets on the backend
  This lookup is used to hook the filters in search to the facets on the backend.

  For example, the Service Level and Design Style are the two facets we search for with
  florists, this mapper says that Design Style is the 'Florist style' facet from this
  definition of the search filters:
    [FLORISTS_TYPE_KEY]: {
      desktop: ['Location', 'Price', 'Availability', 'Service level', 'Design style'],
      extra: [],
  },
*/
const categoriesNameMapper: { [key in FacetParentKey]?: string } = {
  // Venues
  'target-couple-venue-preference': 'Venue type',
  'venue-service-level': 'Included',
  'services-offered': 'Amenities',
  'venue-setting': 'Indoor/outdoor',

  // Photographers
  'package-components-services-offered': 'Services',
  'package-deliverables-offered': "What you'll get",

  // Videographers
  'videographer-package-components': 'Services',
  'videographer-deliverables': "What you'll get",

  // Florists
  'florist-ethical-areas': 'Ethical considerations',
  'florist-style': 'Design style',
  'florist-service-level': 'Services',
  'florist-floral-arrangement': 'Arrangements',
  'florist-arrangement-types': 'Composition',

  // Caterers
  'menu-formats-offered': 'Meal services',
  'caterers-cuisine-types': 'Cuisine type',
  'caterer-dietary-restrictions': 'Dietary accommodations',
  'beverages-related-services': 'Beverage services',
  'caterer-drink-types': 'Drink types',
  'menu-types': 'Menus and packages',

  // Bands & DJs
  'musician-type': 'Type',
  'music-genre': 'Music genre',
  'musician-services': 'Services',
  'music-band-acts': 'Band or ensemble type',
  'music-solo-acts': 'Solo performer type',
  'music-djs': 'DJ type',
  'musician-category': 'Musician type',

  // Beauty Facets
  'hair-beauty-service-category': 'Services',
  'hair-beauty-style': 'Signature style',
  'beauty-services-general': 'Other services',
  'beauty-services-hair': 'Hair services',
  'beauty-services-makeup': 'Makeup services',
  'beauty-services-henna': 'Henna services',
  'beauty-services-spa': 'Spa services',
  'beauty-services-styling': 'Styling services',
  'beauty-services-fitness-wellness': 'Fitness/Wellness services',

  // Bakers
  'bakers-dietary-accommodations': 'Dietary accommodation',
  'bakers-product-types': 'Dessert type',

  // Planners
  'planner-service-level': 'Services',
};

const mapCategoryName = (parentKey: FacetParentKey, name: string): string => {
  return categoriesNameMapper[parentKey]
    ? (categoriesNameMapper[parentKey] as string)
    : capitalize(name);
};
const optionsNamesMapper: { [key in FacetChildKey]?: string } = {
  'musician-type-dj': 'DJ',
  'music-djs-solo-dj': 'Solo DJ',
  'music-djs-group-of-djs': 'Group of DJs',
  'music-genre-r-and-b': 'R&B',
  'musician-staff-available-djs': 'DJs',
  'videographer-deliverables-dvd-or-blue-ray': 'DVD or Blu-ray',
  'videographer-deliverables-usb': 'USB',
  'rental-services-sound-av': 'Sound / AV',
};

const mapOptionName = (childKey: FacetChildKey, name: string): string => {
  return optionsNamesMapper[childKey] ? (optionsNamesMapper[childKey] as string) : capitalize(name);
};

export const reduceTaxonomyWithFacetsToLookup = (
  taxonomiesWithFacets: TaxonomyWithFacetsView[],
  onDuplicate?: (
    taxonomyKey: string,
    existingFacet: SearchableFacet,
    newFacet: SearchableOptionChildView
  ) => void
) => {
  // As we iterate compute a facet looker-upper by slug
  const facetsByVendorType: FacetsByVendorType = {};

  // we get back an array of taxonomies with facet children
  const facetsByVendorTypeAndCategory: FacetsByTaxonomy = taxonomiesWithFacets.reduce(
    (currentResultHash, taxonomyData) => {
      const vendorTypeKey = taxonomyData.key;
      const currentVendorType: Record<string, SearchableFacet> = {};
      facetsByVendorType[vendorTypeKey] = currentVendorType;

      const categoriesForTaxonomy = taxonomyData.facets.reduce(
        (categories, parentFacetsForTaxonomy) => {
          const category: FacetCategory = {
            name: parentFacetsForTaxonomy.name,
            displayName: mapCategoryName(parentFacetsForTaxonomy.key, parentFacetsForTaxonomy.name),
            key: parentFacetsForTaxonomy.key,
            slug: parentFacetsForTaxonomy.key, // should this stay key and we should have a different slug?
            facets: parentFacetsForTaxonomy.facets.map((childFacet) => {
              const { slug } = childFacet;
              const facetForRedux: SearchableFacet = {
                parentKey: parentFacetsForTaxonomy.key,
                name: childFacet.name,
                displayName: mapOptionName(childFacet.key, childFacet.name),
                key: childFacet.key,
                slug: childFacet.slug,
                description: childFacet.description,
              };
              if (slug && currentVendorType[slug]) {
                const existing = currentVendorType[slug];
                onDuplicate && onDuplicate(taxonomyData.key, existing, childFacet);
              }
              currentVendorType[slug] = facetForRedux;
              return facetForRedux;
            }),
          };

          return [...categories, category];
        },
        [] as FacetCategory[]
      );

      return {
        ...currentResultHash,
        [vendorTypeKey]: categoriesForTaxonomy,
      };
    },
    /* start with an empty lookup */
    {} as FacetsByTaxonomy
  );
  return { facetsByVendorType, facetsByVendorTypeAndCategory };
};

const couplesSearchReducer = (
  state: CouplesSearchState = initialState,
  action: CouplesSearchActions
): CouplesSearchState => {
  switch (action.type) {
    case RECEIVED_FACETS_V2: {
      const { facetsByVendorType, facetsByVendorTypeAndCategory } =
        reduceTaxonomyWithFacetsToLookup(action.payload, (taxonomyKey, existingFacet, newFacet) => {
          if (getEnvironment() !== EnvironmentTypes.PRODUCTION) {
            Logger.error(
              `Duplicated slug for ${taxonomyKey}: ${newFacet.key}. Existing slug: ${existingFacet.slug}. Existing key: ${existingFacet.key}. New key: ${newFacet.key}.`
            );
          }
        });
      return { ...state, facets: facetsByVendorTypeAndCategory, facetsByVendorType };
    }

    default:
      return state;
  }
};

export default couplesSearchReducer;
